/**
 * useFactoringData Hook
 *
 * Shared hook for fetching and managing factoring data across components.
 * Checks auth status first before attempting to fetch data.
 * Auto-detects working provider from authenticated providers.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { resolveOtrVerdict } from '@numeo/types-dispatcher-ui'
import {
    FactoringProvider,
    FactoringDataResponse,
    FactoringStatusResponse,
    FactoringLookupError,
    classifyFactoringLookupError,
    fetchFactoringData,
    fetchFactoringDataByCompany,
    fetchFactoringStatus,
    RTSRatingResponse,
    TriumphCreditResponse,
    WexRatingResponse,
    WinfactorRatingResponse,
    GsquaredRatingResponse,
    CapitalDepotRatingResponse,
    CompassRatingResponse,
    TafsRatingResponse,
    EngagedFinancialRatingResponse,
    HaulPayRatingResponse,
    OtrSolutionsRatingResponse,
    FlexentRatingResponse,
    BasicblockRatingResponse,
    EcapitalRatingResponse,
} from 'services/factoring'
import { useFactoringProviderStore, getProvider, setProvider as storeSetProvider } from './factoringProviderStore'

/**
 * An MC that can actually be looked up. Sources hand through '0' and the string 'null' for "absent",
 * and a lookup keyed on either would be a different lookup from the same load with no MC at all.
 * Shared so the identity a consumer builds and the one a fetch stamps normalise the same way — a
 * divergence here compares unequal forever, which reads as "the answer is always stale".
 */
const usableMcNumber = (mcNumber: string | null | undefined): string | null => (mcNumber && mcNumber !== '0' && mcNumber !== 'null' ? mcNumber : null)

/**
 * The lookup a factoring answer belongs to: the provider asked, and the key it was asked about.
 *
 * Exported so consumers compare against the SAME construction rather than reassembling the format —
 * a consumer-side copy that drifted would silently compare unequal forever, which reads as "the answer
 * is always stale" and suppresses every report.
 *
 * It normalises both keys for that reason. The hook trims the company name before looking anything up,
 * so an untrimmed one from a caller would build a different identity for the same question. Same for the
 * '0'/'null' MC placeholders. Doing it here means a caller cannot get it wrong by passing what it has.
 */
export const factoringLookupIdentity = (
    provider: FactoringProvider | null | undefined,
    mcNumber: string | null | undefined,
    companyName: string | null | undefined
): string => `${provider ?? ''}|${usableMcNumber(mcNumber) ?? ''}|${companyName?.trim() || ''}`

/**
 * Get list of authenticated providers from status response.
 * eCapital is its rating connection, the one the chips and the extension's dropdown connect (Settings' accounting one is not
 * read here): an eCapital-only invoicing project never lists it, so a lookup never ends in "no rating" for it.
 * Engaged Financial is a public API → always authenticated, listed last so real providers take precedence.
 */
export function getAuthenticatedProviders(status: FactoringStatusResponse['data'] | null): FactoringProvider[] {
    const authenticated: FactoringProvider[] = []
    if (!status) {
        authenticated.push(FactoringProvider.ENGAGED_FINANCIAL)
        return authenticated
    }

    if (status.rts?.connected && status.rts?.isValid) authenticated.push(FactoringProvider.RTS)
    if (status.triumph?.connected && status.triumph?.isValid) authenticated.push(FactoringProvider.TRIUMPH)
    if (status.wex?.connected && status.wex?.isValid) authenticated.push(FactoringProvider.WEX)
    if (status.winfactor?.connected && status.winfactor?.isValid) authenticated.push(FactoringProvider.WINFACTOR)
    if (status.gsquared?.connected && status.gsquared?.isValid) authenticated.push(FactoringProvider.GSQUARED)
    if (status.capital_depot?.connected && status.capital_depot?.isValid) authenticated.push(FactoringProvider.CAPITAL_DEPOT)
    if (status.compass?.connected && status.compass?.isValid) authenticated.push(FactoringProvider.COMPASS)
    if (status.tafs?.connected && status.tafs?.isValid) authenticated.push(FactoringProvider.TAFS)
    if (status.haul_pay?.connected && status.haul_pay?.isValid) authenticated.push(FactoringProvider.HAUL_PAY)
    if (status.otr_solutions?.connected && status.otr_solutions?.isValid) authenticated.push(FactoringProvider.OTR_SOLUTIONS)
    if (status.flexent?.connected && status.flexent?.isValid) authenticated.push(FactoringProvider.FLEXENT)
    if (status.basicblock?.connected && status.basicblock?.isValid) authenticated.push(FactoringProvider.BASICBLOCK)
    if (status.ecapital?.connected && status.ecapital?.isValid) authenticated.push(FactoringProvider.ECAPITAL)
    // Outgo (DAT) needs no auth for factorability — the flag is on the load itself, not a
    // per-broker API — so it's always selectable, like Engaged Financial.
    authenticated.push(FactoringProvider.OUTGO)
    // Engaged Financial needs no auth — always include but last so real providers take precedence
    authenticated.push(FactoringProvider.ENGAGED_FINANCIAL)

    return authenticated
}

type FactoringData = FactoringDataResponse

interface FactoringOutcome {
    data: FactoringData | null
    authError: boolean
    lookupError: Exclude<FactoringLookupError, 'auth'> | null
    /** Which lookup produced this — see `factoringLookupIdentity`. */
    identity: string
}

const NO_OUTCOME: Omit<FactoringOutcome, 'identity'> = { data: null, authError: false, lookupError: null }

/**
 * A rating call answered 401/403: the provider's stored session is dead, which is a different thing
 * from the broker having no rating. Distinct from `null` so the callers below can raise a Reconnect
 * CTA instead of reporting "no rating for this MC" on a session the user can actually fix.
 */
const AUTH_FAILED = Symbol('factoring-auth-failed')

// Rating color utilities
const getRatingColor = (rating: unknown): string => {
    if (typeof rating !== 'string') return '#9ca3af' // gray-400
    switch (rating.toUpperCase()) {
        case 'A':
        case 'B':
        case 'C':
            return '#22c55e' // green-500
        case 'D':
        case 'N':
            return '#f59e0b' // amber-500
        case 'F':
        case 'I':
            return '#ef4444' // red-500
        default:
            return '#9ca3af' // gray-400
    }
}

const getTriumphColor = (status: unknown): string => {
    if (typeof status !== 'string') return '#6b7280'
    const s = status.toUpperCase()
    if (s === 'GREEN') return '#22c55e'
    if (s === 'RED') return '#ef4444'
    return '#6b7280'
}

const getTriumphLabel = (status: unknown): string => {
    if (typeof status !== 'string') return 'Unknown'
    const s = status.toUpperCase()
    if (s === 'GREEN') return 'Approved'
    if (s === 'RED') return 'Denied'
    if (s === 'NOBUY') return 'No Buy'
    return 'Unknown'
}

interface FactoringDisplayData {
    rating: string
    color: string
    loadsCount?: string
    creditScore?: string
    daysToPay?: number
    companyName?: string
    /** Backend-resolved semantic tone (OTR) — the chip colors from this instead of parsing the grade. */
    statusTone?: 'success' | 'warning' | 'danger' | 'muted'
}

interface UseFactoringDataResult {
    selectedProvider: FactoringProvider | null
    /** The lookup the outcome fields below answer — see `factoringLookupIdentity`. */
    outcomeIdentity: string
    displayData: FactoringDisplayData
    isLoading: boolean
    /** True only when the provider is NOT authenticated. UI shows a Connect CTA. */
    authError: boolean
    /** Broker-specific failure after authentication. Only a genuine 404 is `not_found`; upstream
     *  failures stay distinguishable so the UI never hides an outage as no data. */
    lookupError: Exclude<FactoringLookupError, 'auth'> | null
    setProvider: (provider: FactoringProvider) => void
    refresh: () => Promise<void>
}

interface UseFactoringDataOptions {
    /** Use one chip-local provider without touching the global Load Hub / AI Hub selection. */
    provider?: FactoringProvider
    /** Fallback broker company-name lookup for no-MC loads and providers that support it (RTS, TAFS, eCapital). */
    companyName?: string | null
}

export function useFactoringData(mcNumber: string | null, opts?: UseFactoringDataOptions): UseFactoringDataResult {
    const forced = opts?.provider
    const companyName = opts?.companyName?.trim() || null
    // SoT: read from the Zustand store. Local writes go through storeSetProvider
    // so every consumer (this hook, AgenticFactoringProviderButton, useSearchForm)
    // sees the same value. A forced provider overrides it CHIP-LOCALLY (never written back).
    const storeProvider = useFactoringProviderStore((s) => s.provider)
    const provider = forced ?? storeProvider
    // `data`, `authError` and `lookupError` are ONE state, and it carries the lookup it answers.
    //
    // They used to be three, which let them describe different requests at the same time. Nothing here
    // waits for the caller: `provider` is derived during render, so it moves a commit before any fetch
    // reacts, and the non-forced paths guard late responses on `mountedRef` alone — so a superseded
    // answer can still land. A consumer reading `data` therefore had no way to ask the only question
    // that matters: is this the answer to what I am asking about now? Writing the three together with
    // the identity they belong to makes them unable to disagree, and lets the consumer compare.
    const [outcome, setOutcome] = useState<FactoringOutcome>({ ...NO_OUTCOME, identity: '' })
    const { data, authError, lookupError } = outcome
    const [isLoading, setIsLoading] = useState(false)

    /**
     * The lookup whose answer the chip is waiting for: each lookup takes the next number when it starts, and only the newest one's
     * answer is written down. An older one can come back after the chip moved to another provider or broker, and written down it
     * would sit under the new question's name (a slow RTS answer shown as TAFS's verdict), and ending the spinner on it would show
     * "Unknown" while the lookup asked is still running.
     *
     * It also keeps the question that lookup asks (`null` while the first-time detection picks a provider), so a branch that asks
     * nothing new can tell whether the lookup running is still for the current question.
     */
    const latestLookup = useRef(0)
    const latestQuestion = useRef<string | null>(null)
    const startLookup = useCallback((identity: string | null) => {
        latestQuestion.current = identity
        return ++latestLookup.current
    }, [])

    /**
     * A new attempt at `identity` is starting: nothing is known about it yet.
     *
     * Distinct from `settle` because a retry of the SAME identity must not inherit the last attempt's
     * verdict — a re-fetch after an auth error that then succeeds would otherwise merge a grade onto a
     * still-true `authError` and render Connect over a rating it had just fetched.
     */
    const beginLookup = useCallback((identity: string) => {
        setOutcome({ ...NO_OUTCOME, identity })
    }, [])

    /**
     * Record the result of an attempt against the lookup it answers.
     *
     * A patch answers exactly ONE request, so when it belongs to a different identity than the stored
     * outcome the stored fields are not a base to merge onto — they describe another broker or another
     * provider. Those start from nothing instead.
     */
    const settle = useCallback((lookup: number, identity: string, patch: Partial<Omit<FactoringOutcome, 'identity'>>) => {
        if (lookup !== latestLookup.current) return
        setOutcome((prev) => ({ ...(prev.identity === identity ? prev : NO_OUTCOME), ...patch, identity }))
    }, [])

    /** `lookup` is over. Unless a newer one started since: that one is still running, and keeps the spinner. */
    const finishLoading = useCallback((lookup: number) => {
        if (lookup === latestLookup.current) setIsLoading(false)
    }, [])
    const initializedRef = useRef(false)
    const initializingRef = useRef(false)
    const mountedRef = useRef(true)

    // Track mount state
    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    // Fetch data for a specific provider
    const fetchData = useCallback(async (p: FactoringProvider, mc: string): Promise<FactoringData | typeof AUTH_FAILED | null> => {
        try {
            const response = await fetchFactoringData(p, mc)
            if (response?.success && response.data) return response
        } catch (error) {
            // The status endpoint only confirms a credentials document exists, so it can report a
            // provider as connected while the rating call rejects its stored session. Collapsing
            // that into null told the user the broker had no rating and left them no way to act.
            const status = (error as { response?: { status?: number } })?.response?.status
            if (status === 401 || status === 403) {
                console.warn(`[FactoringService] ${p} rejected the stored session for MC ${mc} — reconnect required`)
                return AUTH_FAILED
            }
            console.error(`[FactoringService] Failed to fetch ${p} data for MC ${mc}:`, error)
        }
        return null
    }, [])

    // Auth-only check — used when mcNumber is null (no broker context, e.g. search form)
    const checkAuthOnly = useCallback(
        async (p: FactoringProvider) => {
            // No MC on this path by contract. The company name still belongs in the identity: it is
            // part of the question the caller asked, so leaving it out would build a different key from
            // the one the caller compares against.
            const identity = factoringLookupIdentity(p, null, companyName)
            const lookup = startLookup(identity)
            setIsLoading(true)
            beginLookup(identity)

            const status = await fetchFactoringStatus()
            if (!mountedRef.current) return
            const authenticatedProviders = getAuthenticatedProviders(status)

            if (!authenticatedProviders.includes(p)) {
                settle(lookup, identity, { authError: true })
            }
            finishLoading(lookup)
        },
        [companyName, settle, finishLoading, beginLookup, startLookup]
    )

    // Fetch data with auth check
    const fetchWithAuthCheck = useCallback(
        async (p: FactoringProvider, mc: string) => {
            const identity = factoringLookupIdentity(p, mc, companyName)
            const lookup = startLookup(identity)
            setIsLoading(true)
            beginLookup(identity)

            // Check if provider is authenticated before making API call
            const status = await fetchFactoringStatus()
            if (!mountedRef.current) return
            const authenticatedProviders = getAuthenticatedProviders(status)

            if (!authenticatedProviders.includes(p)) {
                console.warn(`[FactoringService] Provider '${p}' is not authenticated, skipping data fetch`)
                settle(lookup, identity, { authError: true })
                finishLoading(lookup)
                return
            }

            // Provider is authenticated - fetch data. A null result here means
            // "no rating for this broker via this provider," NOT an auth failure.
            // Setting authError here would render a misleading Connect CTA when
            // the user is already connected and just lacks a grade for this MC.
            const result = await fetchData(p, mc)
            if (!mountedRef.current) return
            if (result === AUTH_FAILED) {
                settle(lookup, identity, { authError: true })
            } else if (result) {
                settle(lookup, identity, { data: result })
            } else {
                console.warn(`[FactoringService] Data fetch returned no results for authenticated provider '${p}' — no rating for this MC`)
                settle(lookup, identity, { lookupError: 'not_found' })
            }
            finishLoading(lookup)
        },
        [fetchData, companyName, settle, finishLoading, beginLookup, startLookup]
    )

    const fetchForcedProvider = useCallback(
        async (p: FactoringProvider, rawMc: string | null, name: string | null, isCancelled: () => boolean = () => false) => {
            const mc = usableMcNumber(rawMc)
            const identity = factoringLookupIdentity(p, mc, name)
            const lookup = startLookup(identity)
            if (!mc && !name) {
                beginLookup(identity)
                finishLoading(lookup)
                return
            }

            setIsLoading(true)
            beginLookup(identity)
            try {
                const status = await fetchFactoringStatus()
                if (isCancelled() || !mountedRef.current) return
                if (!getAuthenticatedProviders(status).includes(p)) {
                    settle(lookup, identity, { authError: true })
                    return
                }

                const result = mc ? await fetchFactoringData(p, mc) : await fetchFactoringDataByCompany(p, name as string)
                if (isCancelled() || !mountedRef.current) return
                if (result?.success && result.data) settle(lookup, identity, { data: result })
                else settle(lookup, identity, { lookupError: 'not_found' })
            } catch (error) {
                if (isCancelled() || !mountedRef.current) return
                const errorKind = classifyFactoringLookupError(error)
                if (errorKind === 'auth') settle(lookup, identity, { authError: true })
                else {
                    if (errorKind === 'unavailable') console.error('[FactoringService] chip-local provider unavailable', error)
                    settle(lookup, identity, { lookupError: errorKind })
                }
            } finally {
                if (!isCancelled() && mountedRef.current) finishLoading(lookup)
            }
        },
        [settle, finishLoading, beginLookup, startLookup]
    )

    // Chip-local provider path. It never reads/writes the global store or auto-detects, so changing the
    // box-truck provider cannot clobber the app-wide Load Hub / AI Hub selection.
    useEffect(() => {
        if (!forced) return
        let cancelled = false
        void fetchForcedProvider(forced, mcNumber, companyName, () => cancelled)
        return () => {
            cancelled = true
        }
    }, [forced, mcNumber, companyName, fetchForcedProvider])

    // Initialize: check auth status first, then load saved provider or auto-detect
    useEffect(() => {
        if (forced) {
            // A chip can START forced and later release — FactoringApprovalChip forces Capital Depot
            // from a prefetched grade only while no global provider is chosen, so picking one from its
            // dropdown drops the force. Without this, `initializedRef` was still false at that moment
            // and first-time initialization ran LATE: it re-fetched the provider `setProvider` had just
            // fetched, and when that provider wasn't connected it fell into the auto-detect loop and
            // storeSetProvider'd past the user's choice. Being forced IS the initialization.
            initializedRef.current = true
            return
        }
        if (initializedRef.current) return
        if (!mcNumber || mcNumber === '0' || mcNumber === 'null') return

        initializedRef.current = true
        initializingRef.current = true
        const lookup = startLookup(null)
        setIsLoading(true)

        const initialize = async () => {
            // First, check which providers are authenticated
            const status = await fetchFactoringStatus()
            if (!mountedRef.current) return
            const authenticatedProviders = getAuthenticatedProviders(status)

            // If no providers are authenticated, show auth error
            if (authenticatedProviders.length === 0) {
                storeSetProvider(FactoringProvider.RTS) // Default to RTS for auth dialog
                settle(lookup, factoringLookupIdentity(FactoringProvider.RTS, mcNumber, companyName), { authError: true })
                finishLoading(lookup)
                initializingRef.current = false
                return
            }

            // Check if saved provider is authenticated
            const saved = getProvider()
            if (saved && authenticatedProviders.includes(saved)) {
                // Saved provider is authenticated, use it and fetch data
                storeSetProvider(saved)
                const result = await fetchData(saved, mcNumber)
                if (!mountedRef.current) return
                if (result === AUTH_FAILED) {
                    settle(lookup, factoringLookupIdentity(saved, mcNumber, companyName), { authError: true })
                    finishLoading(lookup)
                    initializingRef.current = false
                    return
                }
                if (result) {
                    settle(lookup, factoringLookupIdentity(saved, mcNumber, companyName), { data: result })
                } else {
                    // Auth is valid; just no rating for this broker. Don't flip
                    // authError — that would render a Connect CTA when the user
                    // is already connected. Use the not-found state to signal "Unknown rating."
                    console.warn(`[FactoringService] Data fetch returned no results for saved provider '${saved}' — no rating for this MC`)
                    settle(lookup, factoringLookupIdentity(saved, mcNumber, companyName), { lookupError: 'not_found' })
                }
                finishLoading(lookup)
                initializingRef.current = false
                return
            }

            // No saved provider or saved provider not authenticated
            // Try authenticated providers in order
            // A rejected session is not a reason to stop auto-detecting: another provider may still
            // answer, and it wins. Which one was rejected is remembered rather than just that one
            // was, so the CTA below can name it.
            let rejectedProvider: FactoringProvider | null = null
            for (const p of authenticatedProviders) {
                const result = await fetchData(p, mcNumber)
                if (!mountedRef.current) return
                if (result === AUTH_FAILED) {
                    if (!rejectedProvider) rejectedProvider = p
                    continue
                }
                if (result) {
                    storeSetProvider(p)
                    settle(lookup, factoringLookupIdentity(p, mcNumber, companyName), { data: result })
                    finishLoading(lookup)
                    initializingRef.current = false
                    return
                }
            }

            if (!mountedRef.current) return
            // Nothing answered. Select the provider that rejected its session, so Reconnect is shown
            // against the one the user can actually fix: selecting the first authenticated provider
            // and flagging authError would offer to reconnect a provider whose auth is fine.
            if (rejectedProvider) {
                storeSetProvider(rejectedProvider)
                settle(lookup, factoringLookupIdentity(rejectedProvider, mcNumber, companyName), { authError: true })
                finishLoading(lookup)
                initializingRef.current = false
                return
            }
            // All authenticated providers failed to return data
            // Use first authenticated provider but show no data
            storeSetProvider(authenticatedProviders[0])
            finishLoading(lookup)
            initializingRef.current = false
        }

        initialize()
    }, [mcNumber, fetchData, forced, companyName, settle, finishLoading, startLookup])

    // Fetch data when provider or MC changes (but not during initialization)
    useEffect(() => {
        if (forced) return
        // Skip if still initializing - initialization handles the first fetch
        if (initializingRef.current) return
        if (!provider || !mcNumber || mcNumber === '0' || mcNumber === 'null') {
            const identity = factoringLookupIdentity(provider, mcNumber, companyName)
            // Nothing to ask here. A lookup for another question must not land on this one, so it is dropped and its spinner ends
            // now. One for THIS question keeps running: picking a provider without an MC checks its connection (`checkAuthOnly`),
            // and dropping that answer left the chip on "Unknown" with no Connect.
            if (latestQuestion.current !== identity) finishLoading(startLookup(identity))
            beginLookup(identity)
            return
        }
        if (data) return // Already have data

        fetchWithAuthCheck(provider, mcNumber)
    }, [provider, mcNumber, data, fetchWithAuthCheck, forced, companyName, beginLookup, finishLoading, startLookup])

    // Clear cached MC data when the global provider changes (e.g. another
    // component called `setProvider`). The Zustand store handles cross-tab
    // sync via its `persist` middleware's storage-event handling — no manual
    // window listeners needed.
    useEffect(() => {
        if (forced) return
        beginLookup(factoringLookupIdentity(provider, mcNumber, companyName))
    }, [provider, forced, mcNumber, companyName, beginLookup])

    // Set provider manually
    const setProvider = useCallback(
        (p: FactoringProvider) => {
            storeSetProvider(p)
            beginLookup(factoringLookupIdentity(p, mcNumber, companyName))
            if (mcNumber) {
                fetchWithAuthCheck(p, mcNumber)
            } else {
                // No MC number (search form context) — check auth only so the connect dialog can fire
                checkAuthOnly(p)
            }
        },
        [fetchWithAuthCheck, checkAuthOnly, mcNumber, companyName, beginLookup]
    )

    const refresh = useCallback(async () => {
        beginLookup(factoringLookupIdentity(provider, mcNumber, companyName))
        if (!provider) return
        if (forced) return fetchForcedProvider(forced, mcNumber, companyName)
        if (mcNumber) await fetchWithAuthCheck(provider, mcNumber)
        else await checkAuthOnly(provider)
    }, [provider, forced, mcNumber, companyName, fetchForcedProvider, fetchWithAuthCheck, checkAuthOnly, beginLookup])

    // Calculate display data
    const getDisplayData = (): FactoringDisplayData => {
        const empty = { rating: '–', color: '#9ca3af' }
        // `data` presence is the real gate — a forced company-name lookup has data but no mcNumber.
        if (!provider || !data?.data) return empty

        if (provider === FactoringProvider.RTS) {
            const d = (data as RTSRatingResponse).data
            const loadsCount = d?.loadsCount?.toString() ?? d?.invoicesIncludedTotal?.toString()
            return {
                rating: d?.rating || '–',
                color: getRatingColor(d?.rating || ''),
                daysToPay: d?.daysToPay ?? d?.averageDaysToPayPast60,
                companyName: d?.companyName ?? d?.name,
                loadsCount,
            }
        }

        if (provider === FactoringProvider.TRIUMPH) {
            const d = (data as TriumphCreditResponse).data
            if (d?.notFound) {
                return { rating: 'Not Found', color: '#f59e0b' }
            }
            return {
                rating: d?.fmcsaBroker || getTriumphLabel(d?.creditStatus || ''),
                color: getTriumphColor(d?.creditStatus || ''),
                daysToPay: d?.averageDaysToPay,
                companyName: d?.legalName,
                loadsCount: d?.purchasedInvoiceCount?.toString(),
            }
        }

        if (provider === FactoringProvider.WEX) {
            const d = (data as WexRatingResponse).data
            return {
                rating: d?.status?.rating || '–',
                color: d?.status?.color || '#9ca3af',
                loadsCount: d?.daysToPayAll?.payments || d?.loadsCount,
                companyName: d?.name,
            }
        }

        if (provider === FactoringProvider.WINFACTOR) {
            const d = (data as WinfactorRatingResponse).data
            const status = d?.creditStatus?.toLowerCase() || ''
            // Extension maps: Buy → green, Call First → orange, No Buy → red
            const color = status.includes('no buy') ? '#ef4444' : status.includes('call first') ? '#f59e0b' : status.includes('buy') ? '#22c55e' : '#9ca3af'
            return {
                rating: d?.creditStatus || '–',
                color,
                loadsCount: d?.creditScore != null ? String(d.creditScore) : undefined,
            }
        }

        if (provider === FactoringProvider.GSQUARED) {
            const d = (data as GsquaredRatingResponse).data
            const status = d?.creditStatus?.toLowerCase() || ''
            const color = status.includes('no buy') ? '#ef4444' : status.includes('call first') ? '#f59e0b' : status.includes('buy') ? '#22c55e' : '#9ca3af'
            return {
                rating: d?.creditStatus || '–',
                color,
                loadsCount: d?.creditScore != null ? String(d.creditScore) : undefined,
            }
        }

        if (provider === FactoringProvider.CAPITAL_DEPOT) {
            const d = (data as CapitalDepotRatingResponse).data
            const status = d?.creditStatus?.toLowerCase().trim() || ''
            const creditScore = d?.creditScore != null ? String(d.creditScore) : undefined
            // Substring matching, no-buy first: the portal decorates a verdict ("No Buy - Do Not Load"),
            // and an exact match rendered a refused broker as an unrated dash. Same cascade as
            // `capitalDepotGrade` on the server and `classifyCapitalDepotStatus` in the extension.
            if (status.includes('no buy') || status.includes('nobuy')) {
                return { rating: 'No Buy', color: '#ef4444', creditScore }
            }
            if (status.includes('call')) {
                return { rating: 'Call First', color: '#f59e0b', creditScore }
            }
            if (status.includes('buy')) {
                return { rating: 'Buy', color: '#22c55e', creditScore }
            }
            return { rating: '–', color: '#9ca3af' }
        }

        if (provider === FactoringProvider.COMPASS) {
            const d = (data as CompassRatingResponse).data
            // NoBuy description takes precedence (broker blocked)
            if (d?.noBuyDescription) {
                return { rating: 'No Buy', color: '#ef4444', companyName: d.companyName, loadsCount: String(d.creditScore ?? '') }
            }
            return {
                rating: d?.creditRating || '–',
                color: getRatingColor(d?.creditRating),
                companyName: d?.companyName,
                loadsCount: d?.creditScore != null ? String(d.creditScore) : undefined,
                daysToPay: d?.daysToPayAvg != null ? Number(d.daysToPayAvg) : undefined,
            }
        }

        if (provider === FactoringProvider.TAFS) {
            const d = (data as TafsRatingResponse).data
            const status = d?.status?.toLowerCase() || ''
            const color = status.includes('no buy') ? '#ef4444' : status.includes('buy') ? '#22c55e' : '#9ca3af'
            return {
                rating: d?.status || '–',
                color,
                companyName: d?.companyName,
                loadsCount: d?.creditScore != null ? String(d.creditScore) : undefined,
            }
        }

        if (provider === FactoringProvider.ENGAGED_FINANCIAL) {
            const d = (data as EngagedFinancialRatingResponse).data
            if (d?.noBuy) {
                return { rating: 'No Buy', color: '#ef4444', companyName: d?.name }
            }
            return {
                rating: d?.name ? 'Approved' : '–',
                color: d?.name ? '#22c55e' : '#9ca3af',
                companyName: d?.name,
            }
        }

        if (provider === FactoringProvider.HAUL_PAY) {
            const d = (data as HaulPayRatingResponse).data
            const approved = d.creditApproved?.toLowerCase() || ''
            const color = approved.includes('approved') ? '#22c55e' : approved ? '#ef4444' : '#9ca3af'
            // HaulPay doesn't return a "loads count" — we reuse the loadsCount field to surface
            // the credit-available dollar value in the same UI slot. The label is rendered upstream
            // and intentionally generic ("Loads" can mean either count or dollars depending on provider).
            return {
                rating: d.creditApproved || '–',
                color,
                companyName: d.companyName,
                daysToPay: d.avgPay,
                loadsCount: d.creditAvailable ? `$${d.creditAvailable.toLocaleString()}` : undefined,
            }
        }

        if (provider === 'otr_solutions') {
            const d = (data as OtrSolutionsRatingResponse).data
            // Verdict is decided by the BACKEND (OTR's authoritative CreditCheckAsync). Resolve the SAME
            // way the server does — `resolveOtrVerdict` applies the `noBuy` fallback for legacy cache rows —
            // so the text, color, and tone all agree (a legacy row can't show "Not Approved" on a green chip).
            const v = resolveOtrVerdict({ approved: d?.approved, noBuy: d?.noBuy })
            return {
                rating: d?.approvalText || v.grade || '–',
                color: d?.color || v.color || '#9ca3af',
                statusTone: d?.tone ?? v.tone,
                companyName: d?.companyName,
                daysToPay: d?.paymentTerms,
                loadsCount: d?.creditLimit != null ? `$${d.creditLimit.toLocaleString()}` : undefined,
            }
        }

        if (provider === FactoringProvider.BASICBLOCK) {
            const d = (data as BasicblockRatingResponse).data
            // Verdict comes from `status` alone — mirrors `basicblockGrade` on the backend. There is
            // deliberately no secondary figure: BasicBlock's own Broker Check screen shows the company
            // name, the MC and an Approved/Rejected pill, and nothing else. The account-scoped numbers
            // their API also returns (open balance, credit limit) describe THIS carrier's relationship
            // with the broker, not the broker's creditworthiness, so they are not a rating signal.
            const verdict = (d.status || '').toLowerCase().trim()
            const rating = verdict === 'approved' ? 'Approved' : verdict === 'rejected' ? 'Denied' : 'N/A'
            const color = verdict === 'approved' ? '#22c55e' : verdict === 'rejected' ? '#ef4444' : '#9ca3af'
            return {
                rating,
                color,
                companyName: d.companyName,
            }
        }

        if (provider === FactoringProvider.FLEXENT) {
            const d = (data as FlexentRatingResponse).data
            // Verdict comes from `status` alone — mirrors `flexentGrade` on the backend. The response
            // carries no secondary figure to show: the backend drops the four wire fields nothing renders
            // (see `FlexentCreditCheckOutput`), so there is deliberately no value for `loadsCount` here.
            // `daysToPay` is left out for the same reason. Flexent grades on the verdict and nothing
            // else, and appending "· 35d pay" to the chip made it claim a figure the panel never shows.
            const verdict = (d.status || '').toUpperCase().trim()
            const rating = verdict === 'APPROVED' ? 'Approved' : verdict === 'DENIED' ? 'Denied' : 'N/A'
            const color = verdict === 'APPROVED' ? '#22c55e' : verdict === 'DENIED' ? '#ef4444' : '#9ca3af'
            return {
                rating,
                color,
                companyName: d.companyName,
            }
        }

        if (provider === FactoringProvider.ECAPITAL) {
            const d = (data as EcapitalRatingResponse).data
            // No verdict is no eCapital answer: never draw one from it.
            if (typeof d?.eligible !== 'boolean') return empty
            // Verdict from `eligible` alone, as on the portal's "Run Credit" page; the score letter rides along like Capital Depot's.
            return {
                rating: d.eligible ? 'Approved' : 'Denied',
                color: d.eligible ? '#22c55e' : '#ef4444',
                statusTone: d.eligible ? 'success' : 'danger',
                creditScore: d.creditScore,
                companyName: d.name || undefined,
            }
        }

        return empty
    }

    return {
        selectedProvider: provider,
        displayData: getDisplayData(),
        /** The lookup `displayData` / `authError` / `lookupError` answer. Compare with
         *  `factoringLookupIdentity(selectedProvider, mcNumber, companyName)` to tell a fresh answer
         *  from one left over from a request the caller has already moved off. */
        outcomeIdentity: outcome.identity,
        isLoading,
        authError,
        lookupError,
        setProvider,
        refresh,
    }
}
